import { normalizeForSearch } from './textSearch';
import type { InvoiceListItem } from '@repo/shared-types';

/**
 * Predicate behind the invoices-list search box (design:
 * docs/superpowers/specs/2026-08-18-invoice-search-structured-design.md §3).
 * Client-side, over the already fully-loaded (unpaginated) list — no server
 * `?search=`, no debounce (see the design doc's rejected-alternatives note on
 * Meilisearch).
 *
 * Matches, in order:
 * - the invoice code, case-insensitively on the raw string (not
 *   diacritic-folded — codes are ASCII, and folding would risk surprising
 *   matches on unrelated fragments), so "inv-2026" and code fragments work;
 * - the normalized payer/client name;
 * - every linked student's `name` AND `nickname` (nickname included because
 *   this is exactly what nickname exists for — see `InvoiceStudentRef`'s doc
 *   comment in shared-types; the message adapter, by contrast, never shows
 *   it).
 *
 * An empty (or whitespace-only) query matches everything — the box starts
 * empty and the list should show every invoice until the teacher types.
 */
export function matchesInvoiceSearch(
  invoice: Pick<InvoiceListItem, 'invoiceCode' | 'students'> & {
    client?: { name?: string | null } | null;
  },
  query: string,
): boolean {
  const trimmed = query.trim();
  if (trimmed === '') return true;

  if (invoice.invoiceCode.toLowerCase().includes(trimmed.toLowerCase())) return true;

  const normalizedQuery = normalizeForSearch(trimmed);

  if (normalizeForSearch(invoice.client?.name ?? '').includes(normalizedQuery)) return true;

  return invoice.students.some(
    (s) =>
      normalizeForSearch(s.name).includes(normalizedQuery) ||
      (s.nickname !== null && normalizeForSearch(s.nickname).includes(normalizedQuery)),
  );
}
